import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SKIP_401_INTERCEPTOR } from '../../../../interceptors/unauthorized-response.interceptor';
import { AccountDetails, ACCOUNT_DETAILS_MEDIA_TYPE, BackEndAccountDetails } from '../../../classes/account-details';
import { BackEndEnterprise, Enterprise } from '../../../classes/enterprise';
import { BackEndRealm, Realm, REALM_MEDIA_TYPE } from '../../../classes/realm';
import { ContentType } from '../../../enums/content-type.enum';
import { HeaderKey } from '../../../enums/header-key.enum';
import { RealmType } from '../../../enums/realm-type.enum';
import { UserLoginData } from '../../../interfaces/user-login-data.interface';
import { Endpoint, ConfigService } from '../../config';
import { AccessToken, ACCESS_TOKEN_MEDIA_TYPE, BackEndAccessToken } from '../general/access-token';

@Injectable({
  providedIn: 'root'
})
export class LoginApiService {
  private readonly configService = inject(ConfigService);
  private readonly http = inject(HttpClient);

  private readonly url = {
    authTokensUrl: `/auth`,
    enterprisesUrl: `/enterprises`,
    usersUrl: `/users`,
    accountUrl: `/accounts`,
  };

  constructor() {
    this.configService.configLoaded.subscribe(() => {
      const adminServiceUrl = this.configService.getEndpoint(Endpoint.AdminService);
      Object.keys(this.url).forEach(key => {
        const url = this.url[key];
        this.url[key] = `${adminServiceUrl}${url}`;
      });
    });
  }

  public getAccessToken(userLoginData: UserLoginData): Observable<AccessToken> {
    const url = `${this.url.authTokensUrl}/authorise`;
    const credentials = `${userLoginData.emailAddress}:${userLoginData.password}`;
    const token = btoa(credentials);
    const headers = {
      [HeaderKey.Accept]: ACCESS_TOKEN_MEDIA_TYPE,
      [HeaderKey.Authorization]: `Basic ${token}`,
    };
    const params = {
      otp: userLoginData.verificationCode,
    };

    return this.http.get<BackEndAccessToken>(url, { headers, params }).pipe(
      map((backEndAccessToken: BackEndAccessToken) => AccessToken.fromBackend(backEndAccessToken)),
    );
  }

  public getAccessTokenWithCode(code: string): Observable<AccessToken> {
    const url = `${this.url.authTokensUrl}/authorise`;
    const headers = {
      [HeaderKey.Accept]: ACCESS_TOKEN_MEDIA_TYPE,
    };
    const params = {
      code,
    };

    return this.http.get<BackEndAccessToken>(url, { headers, params }).pipe(
      map((backEndAccessToken: BackEndAccessToken) => AccessToken.fromBackend(backEndAccessToken)),
    );
  }

  public getRealms(accessToken: AccessToken): Observable<Realm[]> {
    const url = `${this.url.accountUrl}/my/realms`;
    const headers = {
      [HeaderKey.Accept]: REALM_MEDIA_TYPE,
      [HeaderKey.Authorization]: accessToken.toString(),
    };
    return this.http.get<BackEndRealm[]>(url, { headers }).pipe(
      map((realms: BackEndRealm[]) => realms.map(
        (backEndRealm: BackEndRealm) => new Realm(backEndRealm),
      )),
      map<Realm[], Realm[]>(realms => realms.toSorted((a, b) => {
        const order = [RealmType.Idaas, RealmType.Enterprise];
        return order.indexOf(a.type) - order.indexOf(b.type);
      })),
    );
  }

  public getSelf(accessToken: AccessToken): Observable<AccountDetails> {
    const url = `${this.url.accountUrl}/my`;
    const headers = {
      [HeaderKey.Accept]: ACCOUNT_DETAILS_MEDIA_TYPE,
      [HeaderKey.Authorization]: accessToken.toString(),
    };
    return this.http.get<BackEndAccountDetails>(url, { headers }).pipe(
      map((backEndAccount: BackEndAccountDetails) => new AccountDetails(backEndAccount))
    );
  }

  public getRealmAccessToken(realmId: string, accessToken: AccessToken): Observable<AccessToken>{
    const url = `${this.url.authTokensUrl}/refresh`;
    const headers = {
      [HeaderKey.Accept]: ACCESS_TOKEN_MEDIA_TYPE,
      [HeaderKey.Authorization]: accessToken.refreshTokenString(),
    };
    const params = {
      realm: realmId,
    };
    const context = new HttpContext().set(SKIP_401_INTERCEPTOR, true);

    return this.http.get<BackEndAccessToken>(url, { headers, params, context }).pipe(
      map((backEndAccessToken: BackEndAccessToken) => AccessToken.fromBackend(backEndAccessToken)),
    );
  }

  /**
   * Get the enterprise of a user logging in.
   * @param enterpriseId The user's enterprise id
   * @param accessToken The user's access token
   * @returns The user's enterprise
   */
  public getEnterprise(enterpriseId: string, accessToken: AccessToken): Observable<Enterprise> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Enterprise,
      [HeaderKey.Authorization]: accessToken.toString(),
    };
    return this.http.get<BackEndEnterprise>(url, { headers }).pipe(
      map((backEndEnterprise: BackEndEnterprise) => new Enterprise(backEndEnterprise)),
    );
  }
}
